import { createAsyncThunk } from '@reduxjs/toolkit';
import { setBufferDeliveryAddress } from '../reducers/buffer/deliveryAddress';
import { setBufferDeliveryCoordinates } from '../reducers/buffer/deliveryCoordinates';
import { setBufferLocationDeliveryEstimates } from '../reducers/buffer/locationDeliveryEstimates';
import { getOrderingProvider } from '../selectors/config';
import { $getDeliveryAddressString } from '../selectors/getDeliveryAddressString';
import { $getDeliveryTime } from '../selectors/getDeliveryTime';
import { ASAP_TIME, FAILURE_REASON } from '../constants';
import moment from 'moment';
import { $getStagedPurchaseCount } from '../selectors/getStagedPurchaseCount';
import { isEmpty, get, pickBy } from 'lodash';
import { $getOrderTotals } from '../selectors/getOrderTotals';
import Api, { ApiResponse, FetchParams } from '../utils/Api';
import processDeliveryEstimate from '../utils/processors/processDeliveryEstimate';
import { applyLocationEstimate } from './applyLocationEstimate';
import { SALE_TYPE } from '../constants/saleType';
import { IDeliveryDropOffCoordinatesHttpDto } from '../dtos/interfaces/IDeliveryDropOffCoordinatesHttpDto';

export interface GetDeliveryBestEstimateHttpDto {
  readonly deliveryAddress?: string;
  readonly desiredDeliveryTime?: string;
  readonly locationId?: string;
  readonly multiple?: boolean;
  readonly forceASAPDeliveryEstimate?: boolean;
  readonly saleType?: SALE_TYPE;
  readonly deliveryDropOffCoordinates?: IDeliveryDropOffCoordinatesHttpDto;
}

export const fetchDeliveryEstimate = createAsyncThunk(
  '$fetchDeliveryEstimate',
  async (
    data: GetDeliveryBestEstimateHttpDto,
    { dispatch, getState, rejectWithValue },
  ) => {
    let reason = FAILURE_REASON.UNKNOWN;
    let userReason: string | undefined;
    let systemReason: string | undefined;

    try {
      const {
        deliveryAddress,
        desiredDeliveryTime,
        locationId,
        multiple = false,
        forceASAPDeliveryEstimate = false,
        saleType = SALE_TYPE.DELIVERY,
        deliveryDropOffCoordinates,
      } = data;

      const performMultiple = locationId != null ? false : multiple;

      const addressString =
        deliveryAddress ||
        $getDeliveryAddressString(getState() as EntireFrontendState);

      if (!addressString) {
        reason = FAILURE_REASON.MISSING_PARAMETER;
        throw new Error('address required');
      }

      let deliveryTime =
        desiredDeliveryTime ||
        $getDeliveryTime(getState() as EntireFrontendState) ||
        ASAP_TIME;

      if (deliveryTime !== ASAP_TIME) {
        deliveryTime = moment(deliveryTime).format();
      }

      if (forceASAPDeliveryEstimate) {
        deliveryTime = ASAP_TIME;
      }

      const orderTotals = $getOrderTotals(getState() as EntireFrontendState);

      const total = get(orderTotals, 'purchasesMoneyPrice', 0);

      const stagedPurchaseCount =
        $getStagedPurchaseCount(getState() as EntireFrontendState) || 0;
      const orderingProvider = getOrderingProvider(
        getState() as EntireFrontendState,
      );

      const urlParams = new URLSearchParams(
        //@ts-ignore
        pickBy(
          {
            desiredTime: deliveryTime,
            address: addressString,
            itemTotal: total,
            qtyTotal: stagedPurchaseCount,
            orderingProvider,
            store: locationId,
            multiple: performMultiple,
            saleType,
            latitude: deliveryDropOffCoordinates?.latitude ?? null,
            longitude: deliveryDropOffCoordinates?.longitude ?? null,
          },
          value => value != null,
        ),
      );

      const params: FetchParams = {
        path: `/api/v1/delivery/best-estimate?${urlParams.toString()}`,
        method: 'GET',
      };

      const response: ApiResponse = await Api.fetch(params);

      const rawEstimate = response?.data as RawDeliveryEstimateBase;

      if (!rawEstimate.DeliveryAvailable) {
        if (rawEstimate.DeliveryAddress) {
          reason = FAILURE_REASON.DELIVERY_UNAVAILABLE;
        } else {
          reason = FAILURE_REASON.ADDRESS_NOT_FOUND;
        }

        userReason = rawEstimate.UserNotPossibleReason;
        systemReason = rawEstimate.NotPossibleReason;

        throw new Error(
          `delivery not available: ${rawEstimate.NotPossibleReason}`,
        );
      }

      const { estimateBase, locationEstimates } =
        processDeliveryEstimate(rawEstimate);
      const hasAvailableStores = !isEmpty(locationEstimates);
      if (!hasAvailableStores) {
        reason = FAILURE_REASON.DELIVERY_UNAVAILABLE;
        userReason = rawEstimate.UserNotPossibleReason;
        systemReason = rawEstimate.NotPossibleReason;
        throw new Error(
          'Delivery not available: no location estimates returned',
        );
      }
      dispatch(setBufferDeliveryAddress(estimateBase.address));
      dispatch(setBufferDeliveryCoordinates(estimateBase.coordinates));

      if (performMultiple) {
        dispatch(setBufferLocationDeliveryEstimates(locationEstimates));
      } else {
        dispatch(
          applyLocationEstimate({
            ...locationEstimates[0],
            isASAP: desiredDeliveryTime === ASAP_TIME,
          }),
        );
        dispatch(setBufferLocationDeliveryEstimates(locationEstimates));
      }

      return {
        multiple: performMultiple,
      };
    } catch (e) {
      console.warn('Fetch delivery estimate failed', { e });
      const _userReason = get(e, 'details.json.data.UserNotPossibleReason');
      const _systemReason = get(e, 'details.json.data.NotPossibleReason');
      return rejectWithValue({
        e,
        userReason: userReason || _userReason,
        reason,
        systemReason: systemReason || _systemReason,
      });
    }
  },
);
